import type { OnboardingStep } from "@configs/custom-app-config-schema";
import { cacheKeys } from "@constants/cache-keys";
import { getFilteredExams, getStatesForCategory } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import { useDevice } from "@hooks/useDevice";
import type { ExamDetails, StateDetails } from "@memorang/types";
import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { AppContextCache } from "types/apps";

type SelectType = "category" | "level" | "state";
type OnboardingContextValue = {
	selectedCategoryId: string;
	selectedLevelId: string;
	selectedStateCode: string;
	categoryHasState: boolean;
	stateQuery: string;
	examDate: string;
	stepProgress: number;
	handleSelect: (id: string, type: SelectType) => void;
	handleSearchState: (query: string) => void;
	filteredStates: StateDetails[];
	filteredExams: ExamDetails[];
	stepsToShow: OnboardingStep[];
	handleUpdateProgress: (type: "increment" | "decrement") => void;
	reset: () => void;
};

const OnboardingContext = createContext<Partial<OnboardingContextValue>>({
	selectedCategoryId: "",
	filteredStates: [],
	filteredExams: [],
	stepProgress: 0,
	stepsToShow: [],
});

const OnboardingContextProvider = ({
	children,
}: { children: React.ReactNode }) => {
	const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
	const [selectedLevelId, setSelectedLevelId] = useState<string>("");
	const [selectedStateCode, setSelectedStateCode] = useState<string>("");
	const onboardingSteps = useAppStore((state) => state.onboarding) ?? [];
	const [stepProgress, setStepProgress] = useState<number>(0);
	const [stateQuery, setStateQuery] = useState<string>("");

	const { isMobile } = useDevice();

	const { appContextCacheKey } = useCacheKey();

	const { setCache } = useCache<AppContextCache>({
		key: appContextCacheKey || "",
		defaultData: { selectedStates: [{ code: "", relatedExamsIds: [] }] },
		enabled: Boolean(appContextCacheKey),
		shouldOptimisticUpdate: true,
	});

	const stepsToShow = useMemo(() => {
		const enabledSteps = onboardingSteps.filter((s) => s.enabled);

		const baseSteps = isMobile
			? enabledSteps.filter((s) => (s.isMobile != null ? s.isMobile : true))
			: enabledSteps;

		return baseSteps;
	}, [isMobile, onboardingSteps]);

	const queryClient = useQueryClient();

	const examsList =
		queryClient.getQueryData<ExamDetails[]>([cacheKeys.exams]) ?? [];

	const selectedCategoryHasState = useMemo(() => {
		return examsList.some((exam) =>
			exam.exam.category.some(
				(category) => category.id === selectedCategoryId && category.hasState,
			),
		);
	}, [examsList, selectedCategoryId]);

	const getTotalSteps = useCallback(() => {
		const hasStateOnboardingStep = stepsToShow.some(
			//@ts-ignore will be fixed once psi state changes are deployed
			(step) => step.step === "chooseState",
		);
		const baseLength = stepsToShow.length;

		if (!selectedCategoryId) {
			return hasStateOnboardingStep ? baseLength - 2 : baseLength;
		}

		if (!selectedCategoryHasState) {
			return baseLength - 2;
		}

		if (selectedLevelId === "national") {
			return baseLength - 1;
		}

		return baseLength;
	}, [
		selectedCategoryId,
		selectedLevelId,
		selectedCategoryHasState,
		stepsToShow,
	]);

	const reset = useCallback(() => {
		setSelectedLevelId("");
		setSelectedStateCode("");
		setStateQuery("");
	}, []);

	const handleUpdateProgress = useCallback(
		(type: "increment" | "decrement") => {
			const newProgress =
				stepProgress +
				(type === "increment" ? 1 / getTotalSteps() : -1 / getTotalSteps());
			setStepProgress(newProgress);
		},
		[stepProgress, getTotalSteps],
	);

	const filteredStates = useMemo(() => {
		if (!selectedCategoryId) {
			return [];
		}
		const statesForCategory = getStatesForCategory(
			examsList,
			selectedCategoryId,
		);

		return statesForCategory
			.flat()
			.filter((state) =>
				state.name.toLowerCase().includes(stateQuery.toLowerCase()),
			);
	}, [examsList, selectedCategoryId, stateQuery]);

	const handleSelect = useCallback(
		(id: string, type: SelectType) => {
			switch (type) {
				case "category":
					setSelectedCategoryId(id);
					break;
				case "level":
					setSelectedLevelId(id);
					break;
				case "state": {
					setSelectedStateCode(id);
					if (appContextCacheKey) {
						const relatedExam = examsList.find(
							(exam) =>
								exam.exam.state?.code &&
								exam.exam.state?.code === id &&
								exam.exam.relatedExams,
						);
						setCache({
							selectedStates: [
								{
									code: id,
									relatedExamsIds: relatedExam?.exam.relatedExams ?? [],
								},
							],
						});
					}
					break;
				}
				default:
					break;
			}
		},
		[appContextCacheKey, setCache, examsList],
	);

	const filteredExams = useMemo(() => {
		return getFilteredExams(
			examsList,
			selectedCategoryId,
			selectedLevelId as "state" | "national" | "state-national",
			selectedStateCode,
		);
	}, [examsList, selectedCategoryId, selectedLevelId, selectedStateCode]);

	const memoizedValue = useMemo(
		() => ({
			selectedCategoryId,
			selectedLevelId,
			selectedStateCode,
			categoryHasState: selectedCategoryHasState,
			stateQuery,
			handleSelect,
			filteredStates,
			handleSearchState: setStateQuery,
			filteredExams,
			stepProgress,
			setStepProgress,
			stepsToShow,
			reset,
			handleUpdateProgress,
		}),
		[
			selectedCategoryId,
			selectedLevelId,
			selectedStateCode,
			selectedCategoryHasState,
			stateQuery,
			handleSelect,
			filteredStates,
			filteredExams,
			stepProgress,
			stepsToShow,
			reset,
			handleUpdateProgress,
		],
	);
	return (
		<OnboardingContext.Provider value={memoizedValue}>
			{children}
		</OnboardingContext.Provider>
	);
};

const useOnboardingContext = () => {
	const context = useContext(OnboardingContext);
	if (!context) {
		throw new Error(
			"useOnboardingContext must be used within a OnboardingContextProvider",
		);
	}
	return context;
};
export { OnboardingContextProvider, useOnboardingContext };
